/**
 * MessageSegments Component
 *
 * Renders the message text with inline issue highlights and replacement text.
 * Handles both original view (issue highlights) and revised view (replacement highlights).
 *
 * DD-161: Performance optimization for 8+ highlights
 * - React.memo on IssueHighlight prevents re-renders when only activeIssueId changes
 * - Memoized segment rendering avoids recalculating unchanged text spans
 * - SC2: 8+ highlights render within 16ms frame budget on iPhone 12
 */

import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import type { TextSegment, RevisedSegment } from './types';
import { IssueHighlight } from './IssueHighlight';
import { styles } from './inlineAnnotationsStyles';

export interface MessageSegmentsProps {
  /** Text segments to render (original or revised) */
  segments: (TextSegment | RevisedSegment)[];
  /** Currently active issue ID (for highlight state) */
  activeIssueId: number | null;
  /** Callback when issue highlight is pressed */
  handleHighlightPress: (issueId: number, position: { x: number; y: number; width: number; height: number }) => void;
  /** Optional test ID */
  testID?: string;
}

/**
 * MemoizedNormalText - Prevents re-render of plain text segments
 * when only activeIssueId changes (common interaction path).
 */
const MemoizedNormalText = React.memo(function MemoizedNormalText({
  text,
  segmentKey,
}: {
  text: string;
  segmentKey: string;
}) {
  return (
    <Text key={segmentKey} style={styles.normalText}>
      {text}
    </Text>
  );
});

/**
 * MemoizedReplacementText - Prevents re-render of replacement segments
 */
const MemoizedReplacementText = React.memo(function MemoizedReplacementText({
  text,
  originalText,
  segmentKey,
}: {
  text: string;
  originalText?: string;
  segmentKey: string;
}) {
  return (
    <Text
      key={segmentKey}
      style={[
        styles.normalText,
        styles.revisedReplacementHighlight,
        styles.revisedReplacementText,
      ]}
      accessibilityLabel={
        originalText
          ? `Changed from: ${originalText}`
          : undefined
      }
    >
      {text}
    </Text>
  );
});

/**
 * MessageSegments - Renders text segments with inline highlights
 *
 * Performance: For messages with 8+ highlights, each segment type is
 * wrapped in React.memo to prevent cascading re-renders when only
 * the active highlight changes. Plain text and replacement segments
 * never re-render on interaction changes.
 */
export const MessageSegments = React.memo(function MessageSegments({
  segments,
  activeIssueId,
  handleHighlightPress,
  testID = 'message-segments',
}: MessageSegmentsProps) {
  // Memoize the rendered segments array to avoid recomputation
  // when parent re-renders with same segments reference
  const renderedSegments = useMemo(() => {
    return segments.map((segment, index) => {
      // Original view: IssueHighlight for issues (red/orange)
      if ('isIssue' in segment && segment.isIssue && segment.issue && segment.issueNumber) {
        return (
          <IssueHighlight
            key={`issue-${segment.issue.id}`}
            issue={segment.issue}
            issueNumber={segment.issueNumber}
            isActive={activeIssueId === segment.issue.id}
            onPress={handleHighlightPress}
            testID={`${testID}-highlight-${segment.issue.id}`}
          />
        );
      }

      // Revised view: Green highlight for accepted replacements
      if ('isReplacement' in segment && segment.isReplacement) {
        return (
          <MemoizedReplacementText
            key={`replacement-${index}`}
            segmentKey={`replacement-${index}`}
            text={segment.text}
            originalText={segment.originalText}
          />
        );
      }

      // Normal text (both views)
      return (
        <MemoizedNormalText
          key={`text-${index}`}
          segmentKey={`text-${index}`}
          text={segment.text}
        />
      );
    });
  }, [segments, activeIssueId, handleHighlightPress, testID]);

  return (
    <View style={styles.messageContainer}>
      <Text style={styles.messageText}>
        {renderedSegments}
      </Text>
    </View>
  );
});
